import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defer, forkJoin, map, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { StorageKey, StorageService } from '@dta/shared/services/storage/storage.service';
import { BaseAuthTokensStorageService } from '@shared/shared/auth/data-access/base-auth-tokens-storage.service';
import { IgnorableInterceptor } from '@shared/interceptors/ignorable-interceptor';
import { InterceptorName, InterceptorNameValue } from '@shared/interceptors/common/enum/interceptor-name';
import { UsersInLoopService } from '@shared/modules/user-manager/data-access/users-in-loop.service';

@Injectable()
export class HttpLoopApiAuthInterceptor extends IgnorableInterceptor implements HttpInterceptor {
  override readonly interceptorName: InterceptorNameValue = InterceptorName.loopApiAuthToken;
  private readonly baseAuthTokensStorageService: BaseAuthTokensStorageService = inject(BaseAuthTokensStorageService);
  private readonly storageService: StorageService = inject(StorageService);
  private readonly usersInLoopService: UsersInLoopService = inject(UsersInLoopService);

  private readonly authHeaderName: string = 'Authorization';
  private readonly impersonateHeaderName: string = 'X-Impersonate-User';

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldBeIgnored(request)) {
      return this.ignoreInterceptor(request, next);
    }

    const userId = this.storageService.getItem(StorageKey.activeUserId);

    return defer(() => {
      if (userId) {
        return forkJoin([
          this.baseAuthTokensStorageService.getBearerToken(userId),
          this.usersInLoopService.getUser$(userId)
        ]);
      }

      return of([undefined, undefined] as const);
    }).pipe(
      map(([token, user]) => {
        if (!token) {
          return request.clone();
        }

        // For dev-only super-token sessions, impersonate the active user by its id.
        const impersonateUserId = user?.isSuperUserToken ? userId : undefined;
        return request.clone({
          headers: this.setAuthHeader(request.headers, token, impersonateUserId)
        });
      }),
      switchMap(request => {
        return next.handle(request).pipe(
          catchError(err => {
            return throwError(() => err);
          })
        );
      })
    );
  }

  private setAuthHeader(headers: HttpHeaders, token: string, impersonateUserId?: string): HttpHeaders {
    headers = headers.set(this.authHeaderName, token);

    // Dev-only super-token impersonation: tell the backend which user this super token should act as.
    if (impersonateUserId) {
      headers = headers.set(this.impersonateHeaderName, impersonateUserId);
    }

    return headers;
  }
}
